import React, { useEffect, useMemo, useRef, useState } from "react";
import { ADVERTISER, OVERVIEW, advertiserRowClass } from "../../../../../core/theme/reportTheme";
import { BUSINESS_TYPE_LABELS } from "../../../../../core/data/businessTypes/labels";
import { VISIBILITY_DESCRIPTION } from "../../../../../core/data/marketOverviewText";
import { rankText } from "../../../../../core/data/advertiserOverviewText";
import { RANK_COLUMNS, byRank } from "../../../../../core/data/advertiserSorts";
import { sortRows, nextSort } from "../../../../../core/data/sortRows";
import SortButton from "../../../components/SortButton";

// "Where you rank": the pinned advertiser ±2 (window rows), with #1 as a
// reference row above when the window doesn't include it. Expanded: the full
// ranking, pinned row highlighted and scrolled into view.
// Fixed column widths, so the narrow panel never scrolls sideways.
// highlightKeys: advertisers highlighted in yellow (from the Advertisers filter).
export default function RankPanel({ result, expandButton, expanded, onShowTab, highlightKeys = [] }) {
  const [sort, setSort] = useState(null);
  const pinnedRef = useRef(null);
  const pinKey = result.pinned.key;
  const source = useMemo(
    () => (expanded ? result.picker.filter(p => p.rank != null).map(p => ({ ...p, isPinned: p.key === pinKey })) : result.window.rows),
    [expanded, result.picker, result.window.rows, pinKey]
  );
  const rows = useMemo(() => sortRows(source, sort, RANK_COLUMNS, byRank), [source, sort]);
  const reference = expanded ? null : result.window.reference;
  const start = result.window.rows[0]?.rank;
  const skipped = reference && start > 2 ? (start === 3 ? "rank 2" : `ranks 2–${start - 1}`) : null;
  const sortProps = { sort, onSort: column => setSort(nextSort(sort, column, RANK_COLUMNS)) };

  useEffect(() => {
    if (expanded && pinnedRef.current && pinnedRef.current.scrollIntoView) pinnedRef.current.scrollIntoView({ block: "center" });
  }, [expanded]);

  return (
    <div className={`${OVERVIEW.panel} flex-1`}>
      <div className={OVERVIEW.panelHeader}>
        <div className={OVERVIEW.panelTitleRow}>
          <h3 className={OVERVIEW.panelTitle}>Where you rank</h3>
          <span className={OVERVIEW.panelNote}>{result.rank.position != null ? rankText(result) : "not ranked: not seen"}</span>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          {sort
            ? <button type="button" onClick={() => setSort(null)} className={OVERVIEW.link}>Back to ranking order</button>
            : <button type="button" onClick={() => onShowTab("overview")} className={OVERVIEW.link}>Full ranking</button>}
          {expandButton}
        </div>
      </div>
      <div className={OVERVIEW.panelScroll}>
        <table className="w-full border-separate border-spacing-0 table-fixed">
          <colgroup>
            <col className="w-12" />
            <col />
            <col className="w-28" />
            <col className="w-[4.5rem]" />
            <col className="w-20" />
          </colgroup>
          <thead>
            <tr>
              <th className={ADVERTISER.thFirst}><SortButton column="rank" label="#" {...sortProps} /></th>
              <th className={ADVERTISER.th}><SortButton column="advertiser" label="Advertiser" {...sortProps} /></th>
              <th className={ADVERTISER.th}><SortButton column="type" label="Type" {...sortProps} /></th>
              <th className={ADVERTISER.th} title={`Visibility: ${VISIBILITY_DESCRIPTION}`}><SortButton column="visibility" label="Visibility" {...sortProps} /></th>
              <th className={ADVERTISER.th}><SortButton column="searches" label="Searches" {...sortProps} /></th>
            </tr>
          </thead>
          <tbody>
            {reference && <Row row={reference} highlighted={highlightKeys.includes(reference.key)} />}
            {reference && <tr><td colSpan={5} className={ADVERTISER.divider}>{skipped || " "}</td></tr>}
            {rows.map(r => <Row key={r.key} row={r} highlighted={highlightKeys.includes(r.key)} rowRef={r.isPinned ? pinnedRef : undefined} />)}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Row({ row, rowRef, highlighted }) {
  const pinned = row.isPinned;
  const type = row.businessType?.type;
  const look = advertiserRowClass(pinned, highlighted);
  return (
    <tr ref={rowRef} className={look.row}>
      <td className={`${ADVERTISER.tdFirst} ${look.first}`}>{row.rank}</td>
      <td className={ADVERTISER.td} title={row.displayName}>
        <span className={pinned ? "font-semibold text-slate-900" : "text-slate-900"}>{row.displayName}</span>
      </td>
      <td className={ADVERTISER.td} title={type ? BUSINESS_TYPE_LABELS[type] : undefined}>
        {type ? <span className="block truncate">{BUSINESS_TYPE_LABELS[type]}</span> : "–"}
      </td>
      <td className={`${ADVERTISER.td} font-semibold text-slate-900`}>{row.visibility.score}%</td>
      <td className={`${ADVERTISER.td} text-slate-500 text-xs`}>{row.visibility.searches} of {row.visibility.of}</td>
    </tr>
  );
}
